// /app — workspace home. Ported 1:1 from design-handoff/src/pages/home.jsx.
//
// Layout: hero PageHead + alert strip + 4-KPI grid + 2-col (activity feed |
// quick actions + approvals mini) + brand cards grid.
//
// Backend wiring:
//   - /v1/workspace/stats  → KPIs (PENDING / POSTS_7D / AVG_HEALTH / AI_COST)
//   - /v1/audit?limit=20   → activity feed
//   - /v1/approvals?limit=4 → approvals mini queue
//   - /v1/brands           → brand cards
//
// Greeting time-of-day in the operator's timezone (Europe/Istanbul by default).

import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useBrands } from "@/hooks/use-persona";
import { useActiveBrand } from "@/stores/active-brand";
import { Icons } from "@/components/icons";
import { PageHead, KPI, BrandMark, PlatformChip, Skeleton } from "@/components/primitives";
import { BrandHealthGuide } from "@/components/BrandHealthGuide";
import { useIsMobile } from "@/lib/use-is-mobile";
import type { Brand } from "@/lib/types";
import type { MeResponse } from "@/lib/auth";

export const Route = createFileRoute("/app/")({
  component: HomePage,
});

// ──────────────────────────────────────────────────────────────────────────────
// HomePage
// ──────────────────────────────────────────────────────────────────────────────

type WorkspaceStats = {
  pending_approval: number;
  posts_last_7d: number;
  avg_health: number;
  ai_cost_this_month?: number;
  ai_cost_last_month?: number;
  failing_brands?: number;
};

function HomePage() {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [showHealthGuide, setShowHealthGuide] = useState(false);
  const isMobile = useIsMobile();
  const { data: meRes } = useQuery({
    queryKey: ["me"],
    queryFn: () => api<MeResponse>("/auth/me"),
    staleTime: 60_000,
  });
  const { data: statsRes } = useQuery({
    queryKey: ["workspace", "stats"],
    queryFn: () => api<WorkspaceStats>("/workspace/stats"),
    staleTime: 30_000,
  });
  // brand-service v0.5.43 — workspace-wide health roll-up. Powers the
  // avg_health + failing_brands KPIs which /workspace/stats stubs at 0
  // (the brand-health signal data lives in brand-service).
  const { data: healthRes } = useQuery({
    queryKey: ["workspace", "brand-health"],
    queryFn: () =>
      api<{ avg_health: number; failing_count: number; total: number }>(
        "/workspace/brand-health",
      ),
    staleTime: 60_000,
  });
  // billing-service v0.1.0 — credit pool for the 4th KPI tile. Operator
  // (2026-05-23) said the AI-cost tile belongs in admin panel, not
  // the user dashboard. Replaced with 'Krediler' showing total +
  // monthly allowance — the same data the topbar credit pill reads.
  const { data: creditsRes } = useQuery({
    queryKey: ["billing", "credits"],
    queryFn: () =>
      api<{
        total_balance: number;
        subscription_balance: number;
        topup_balance: number;
        monthly_allowance: number;
        is_unlimited: boolean;
        balance_label: string;
      }>("/billing/credits"),
    staleTime: 30_000,
  });
  const { data: brandsRes } = useBrands();
  const brands = brandsRes?.data ?? [];

  const stats: WorkspaceStats = {
    pending_approval: statsRes?.pending_approval ?? 0,
    posts_last_7d: statsRes?.posts_last_7d ?? 0,
    avg_health: healthRes?.avg_health ?? 0,
    failing_brands: healthRes?.failing_count ?? 0,
    ai_cost_this_month: statsRes?.ai_cost_this_month,
  };

  const greeting = useGreeting(meRes?.user?.display_name, i18n.resolvedLanguage);
  const failingBrandsCount = stats.failing_brands ?? 0;
  const subtitle = i18n.resolvedLanguage === "tr"
    ? `${stats.pending_approval} gönderi ${brands.filter((b) => (b.pending_count ?? 0) > 0).length} markada onay bekliyor. ${failingBrandsCount} marka ilgi istiyor.`
    : `${stats.pending_approval} posts awaiting approval across ${brands.filter((b) => (b.pending_count ?? 0) > 0).length} brands. ${failingBrandsCount} brand${failingBrandsCount === 1 ? "" : "s"} need attention.`;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <PageHead
        eyebrow={i18n.resolvedLanguage === "tr" ? "01 · Workspace" : "01 · Workspace"}
        title={greeting}
        subtitle={subtitle}
        actions={
          <>
            <button className="btn btn-secondary btn-sm">
              <Icons.Filter size={13} /> {i18n.resolvedLanguage === "tr" ? "Filtreler" : "Filters"}
            </button>
            <button className="btn btn-primary btn-sm" onClick={() => navigate({ to: "/app/bulk" })}>
              <Icons.Layers size={13} /> {i18n.resolvedLanguage === "tr" ? "Toplu plan" : "Bulk plan"}
            </button>
          </>
        }
      />

      <div className="scroll-y" style={{ flex: 1, padding: isMobile ? "0 16px 32px" : "0 32px 48px" }}>
        {/* Expired-token banner — reads live state from
            /v1/workspace/social-health (brand-service v0.5.47+) and
            renders one row per (brand, platform) that needs reconnect.
            Replaces the older generic ReconnectAlert. */}
        <SocialReconnectBanner />

        {/* KPI row — 2-col on phones, 4-col on desktop */}
        <div style={{ paddingTop: isMobile ? 16 : 24 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)",
              gap: isMobile ? 8 : 12,
              marginBottom: isMobile ? 16 : 24,
            }}
          >
            <KPI
              label={i18n.resolvedLanguage === "tr" ? "Onay bekleyen" : "Pending approval"}
              value={stats.pending_approval}
              sub={i18n.resolvedLanguage === "tr" ? "geçen haftaya göre" : "vs last week"}
            />
            <KPI
              label={i18n.resolvedLanguage === "tr" ? "Son 7 gün" : "Posts last 7 days"}
              value={stats.posts_last_7d}
              sub={i18n.resolvedLanguage === "tr" ? "tüm markalar" : "across all brands"}
            />
            <KPI
              label={i18n.resolvedLanguage === "tr" ? "Ort. marka sağlığı" : "Avg brand health"}
              value={`${stats.avg_health}%`}
              sub={
                failingBrandsCount > 0
                  ? (i18n.resolvedLanguage === "tr"
                      ? `${failingBrandsCount} marka eksik — tıkla`
                      : `${failingBrandsCount} brand${failingBrandsCount === 1 ? "" : "s"} need attention — tap`)
                  : (i18n.resolvedLanguage === "tr" ? "detay için tıkla" : "tap for details")
              }
              onClick={() => setShowHealthGuide(true)}
            />
            <KPI
              label={i18n.resolvedLanguage === "tr" ? "Krediler" : "Credits"}
              value={creditsRes?.is_unlimited
                ? "∞"
                : creditsRes?.balance_label ?? "—"}
              sub={
                creditsRes?.is_unlimited
                  ? (i18n.resolvedLanguage === "tr" ? "sınırsız plan" : "unlimited plan")
                  : creditsRes?.monthly_allowance != null
                    ? (i18n.resolvedLanguage === "tr"
                      ? `${creditsRes.subscription_balance} / ${creditsRes.monthly_allowance} aylık + ${creditsRes.topup_balance} ek`
                      : `${creditsRes.subscription_balance} / ${creditsRes.monthly_allowance} monthly + ${creditsRes.topup_balance} topup`)
                    : (i18n.resolvedLanguage === "tr" ? "yükleniyor" : "loading")
              }
            />
          </div>

          {/* On phones stack the columns so the activity feed reads
              full-width and quick actions/approvals follow underneath. */}
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: isMobile ? 12 : 16 }}>
            <ActivityFeed />
            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 12 : 16 }}>
              <QuickActions />
              <ApprovalsMini />
            </div>
          </div>
        </div>

        {/* Brand cards grid */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", margin: "32px 0 16px" }}>
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>02 · {i18n.resolvedLanguage === "tr" ? "Markalar" : "Brands"}</div>
            <h2 style={{ fontSize: 22 }}>
              {i18n.resolvedLanguage === "tr" ? `${brands.length} marka` : `All ${brands.length} brands`}
            </h2>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="btn btn-ghost btn-sm">{i18n.resolvedLanguage === "tr" ? "Tümü" : "All"}</button>
            <button className="btn btn-ghost btn-sm">
              {i18n.resolvedLanguage === "tr" ? "İlgi gerekiyor" : "Need attention"}
              <span className="mono" style={{ color: "var(--warning)", marginLeft: 4 }}>{failingBrandsCount}</span>
            </button>
            <button className="btn btn-ghost btn-sm">{i18n.resolvedLanguage === "tr" ? "Sağlıklı" : "Healthy"}</button>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
            gap: 16,
          }}
        >
          {brands.map((b) => (
            <BrandCard key={b.id} brand={b} />
          ))}
          {brands.length === 0 ? (
            <div style={{ gridColumn: "1 / -1" }}>
              <EmptyBrands />
            </div>
          ) : null}
        </div>
      </div>
      {/* Brand-health click-to-guide modal */}
      {showHealthGuide ? <BrandHealthGuide onClose={() => setShowHealthGuide(false)} /> : null}
      {/* Silence unused-import warning. */}
      <span style={{ display: "none" }}>{t("app.name")}</span>
    </div>
  );
}

// Note: the older ReconnectAlert (generic "a brand has a broken
// connection" message keyed off failing_brands) was removed when
// SocialReconnectBanner replaced it. Banner reads live state from
// /v1/workspace/social-health and shows one row per affected
// (brand, platform).

// ──────────────────────────────────────────────────────────────────────────────
// SocialReconnectBanner — live alert for expired / reauth-required
// social tokens, one row per (brand, platform). Reads from
// /v1/workspace/social-health which only returns rows that NEED
// action — so an empty array means everything is healthy and we
// render nothing.
// ──────────────────────────────────────────────────────────────────────────────

type ExpiredAccount = {
  brand_id: string;
  brand_name: string;
  account_id: string;
  platform: string;
  display_name: string;
  username?: string;
  reason: "expired" | "reauth_required";
  expired_at?: string;
};

const PLATFORM_LABEL: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  x: "X",
  linkedin: "LinkedIn",
  telegram: "Telegram",
};

function SocialReconnectBanner() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ["workspace", "social-health"],
    queryFn: () =>
      api<{ count: number; accounts: ExpiredAccount[] }>(
        "/workspace/social-health",
      ),
    staleTime: 30_000,
    // brand-service can be old (v0.5.46 and earlier don't ship this
    // endpoint). Treat 404 as "no problems" so the banner just hides.
    retry: false,
  });

  const accounts = data?.accounts ?? [];
  if (accounts.length === 0) return null;

  return (
    <div
      style={{
        background: "linear-gradient(90deg, rgba(239,68,68,0.08), transparent 70%)",
        border: "1px solid rgba(239,68,68,0.25)",
        borderRadius: 10,
        padding: "14px 16px",
        marginTop: 24,
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Icons.Alert size={16} style={{ color: "var(--error)" }} />
        <div style={{ flex: 1, fontSize: 13 }}>
          <b>
            {tr
              ? `${accounts.length} hesap yeniden bağlanmalı`
              : `${accounts.length} account${accounts.length === 1 ? "" : "s"} need${accounts.length === 1 ? "s" : ""} reconnect`}
          </b>
          <span style={{ color: "var(--tx2)", marginLeft: 8 }}>
            {tr
              ? "Token süresi doldu. Yeniden bağlanana kadar bu kanaldaki gönderiler yayınlanmaz."
              : "Token expired. Posts on these channels won't publish until reconnected."}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginLeft: 26 }}>
        {accounts.map((a) => (
          <div
            key={a.account_id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "6px 0",
              fontSize: 12,
            }}
          >
            <span style={{ color: "var(--tx)" }}>{a.brand_name}</span>
            <span style={{ color: "var(--mu)" }}>·</span>
            <span style={{ color: "var(--tx2)" }}>{PLATFORM_LABEL[a.platform] ?? a.platform}</span>
            {a.username ? (
              <>
                <span style={{ color: "var(--mu)" }}>·</span>
                <span className="mono" style={{ fontSize: 11, color: "var(--mu)" }}>
                  @{a.username}
                </span>
              </>
            ) : null}
            <div style={{ flex: 1 }} />
            <button
              className="btn btn-secondary btn-sm"
              style={{ padding: "3px 10px", fontSize: 11 }}
              onClick={() =>
                navigate({
                  to: "/app/brands/$brandId",
                  params: { brandId: a.brand_id },
                  search: { tab: "accounts" },
                })
              }
            >
              {tr ? "Yenile →" : "Reconnect →"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ActivityFeed — content-service v0.2.74 unions publish-attempts +
// approvals + generations into a single feed under /v1/workspace/
// activity. Was wired to /v1/audit which is publish-attempts only.
// ──────────────────────────────────────────────────────────────────────────────

type ActivityEntry = {
  id: string;
  kind: string; // publish | fail | approve | reject | generate | edit | schedule
  actor_label: string;
  text: string;
  brand_id: string;
  post_id?: string;
  occurred_at: string;
};

function ActivityFeed() {
  const { i18n } = useTranslation("common");
  const tr = i18n.resolvedLanguage === "tr";
  const { data: brandsRes } = useBrands();
  const { data: activityRes, isLoading, isError } = useQuery({
    queryKey: ["workspace", "activity", { limit: 6 }],
    queryFn: () => api<{ entries: ActivityEntry[] }>("/workspace/activity?limit=6"),
    staleTime: 15_000,
  });
  const brandMap = new Map(brandsRes?.data?.map((b) => [b.id, b]) ?? []);
  const items = activityRes?.entries ?? [];

  return (
    <div className="card" style={{ padding: 22 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>
            — {i18n.resolvedLanguage === "tr" ? "Etkinlik" : "Activity"}
          </div>
          <h2 style={{ fontSize: 16 }}>
            {i18n.resolvedLanguage === "tr" ? "Workspace'te şu anda" : "Just now across the workspace"}
          </h2>
        </div>
        <button className="btn btn-ghost btn-sm">
          {i18n.resolvedLanguage === "tr" ? "Tümü →" : "All →"}
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column" }}>
        {isLoading ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} h={28} />
            ))}
          </div>
        ) : isError ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--mu)",
              padding: "20px 0",
              textAlign: "center",
            }}
          >
            {tr ? "Etkinlik yüklenemedi." : "Couldn't load activity."}
          </div>
        ) : items.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: "var(--mu)",
              padding: "24px 0",
              textAlign: "center",
              lineHeight: 1.6,
            }}
          >
            {tr
              ? "Henüz etkinlik yok. İlk üretimini yapınca burada görünür — Üret sekmesinden başla."
              : "No activity yet. Your first generation will show here — start from the Generate tab."}
          </div>
        ) : null}
        {items.map((a, i) => {
          const brand = brandMap.get(a.brand_id);
          const meta = activityMeta(a.kind);
          const IconCmp = Icons[meta.icon as keyof typeof Icons] ?? Icons.Info;
          const ago = relTime(a.occurred_at, i18n.resolvedLanguage);
          return (
            <div
              key={a.id}
              style={{
                display: "flex",
                gap: 12,
                padding: "10px 0",
                borderBottom: i < items.length - 1 ? "1px solid var(--b1)" : "none",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  background: "var(--elev)",
                  border: "1px solid var(--b2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: meta.tone,
                  flexShrink: 0,
                }}
              >
                <IconCmp size={13} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                  <b>{a.actor_label}</b>{" "}
                  <span style={{ color: "var(--tx2)" }}>{a.text}</span>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
                  {brand ? <BrandMark brand={brand} size={14} /> : null}
                  <span style={{ fontSize: 11, color: "var(--mu)" }}>{brand?.trade_name ?? "—"}</span>
                  <span className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>· {ago}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Maps the workspace activity event kinds to icon + colour. Backend
// emits one of: publish | fail | approve | reject | generate | edit |
// schedule (see content-service workspace_activity.go).
function activityMeta(kind: string): { icon: string; tone: string } {
  switch (kind) {
    case "publish": return { icon: "Send", tone: "var(--info)" };
    case "fail": return { icon: "Alert", tone: "var(--error)" };
    case "approve": return { icon: "Check", tone: "var(--success)" };
    case "reject": return { icon: "X", tone: "var(--error)" };
    case "generate": return { icon: "Sparkles", tone: "var(--accent)" };
    case "edit": return { icon: "Edit", tone: "var(--tx2)" };
    case "schedule": return { icon: "Clock", tone: "var(--warning)" };
    default: return { icon: "Info", tone: "var(--mu)" };
  }
}

function relTime(iso: string, lang?: string): string {
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 60_000;
  if (lang === "tr") {
    if (diff < 1) return "şimdi";
    if (diff < 60) return `${Math.round(diff)} dk önce`;
    if (diff < 60 * 24) return `${Math.round(diff / 60)} sa önce`;
    return `${Math.round(diff / 60 / 24)} gün önce`;
  }
  if (diff < 1) return "now";
  if (diff < 60) return `${Math.round(diff)}m ago`;
  if (diff < 60 * 24) return `${Math.round(diff / 60)}h ago`;
  return `${Math.round(diff / 60 / 24)}d ago`;
}

// ──────────────────────────────────────────────────────────────────────────────
// QuickActions
// ──────────────────────────────────────────────────────────────────────────────

function QuickActions() {
  const { i18n } = useTranslation("common");
  const navigate = useNavigate();
  const tr = i18n.resolvedLanguage === "tr";
  const acts: { icon: keyof typeof Icons; label: string; sub: string; accent?: boolean; to: string }[] = [
    { icon: "Sparkles", label: tr ? "Üret" : "Generate", sub: tr ? "Tek gönderi" : "Single post", accent: true, to: "/app/generate" },
    { icon: "Layers", label: tr ? "Toplu plan" : "Bulk plan", sub: tr ? "Bir marka için 30 gün" : "30 days for one brand", to: "/app/bulk" },
    { icon: "Plus", label: tr ? "Yeni marka" : "New brand", sub: tr ? "URL yapıştır · 5 dk" : "Paste URL · 5 min onboarding", to: "/app/brands/new" },
    { icon: "Plug", label: tr ? "Hesap bağla" : "Connect account", sub: "IG, FB, X, LinkedIn, TG", to: "/app/integrations" },
  ];
  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="eyebrow" style={{ marginBottom: 14 }}>— {tr ? "Hızlı eylemler" : "Quick actions"}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {acts.map((a) => {
          const I = Icons[a.icon];
          return (
            <button
              key={a.label}
              onClick={() => navigate({ to: a.to })}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 8,
                transition: "background 150ms",
                textAlign: "left",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--elev)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: a.accent ? "var(--accent-soft)" : "var(--elev)",
                  color: a.accent ? "var(--accent)" : "var(--tx2)",
                  border: a.accent ? "1px solid var(--red-border)" : "1px solid var(--b2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <I size={15} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{a.label}</div>
                <div style={{ fontSize: 11, color: "var(--mu)" }}>{a.sub}</div>
              </div>
              <Icons.ArrowRight size={13} style={{ color: "var(--mu)" }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// ApprovalsMini
// ──────────────────────────────────────────────────────────────────────────────

// Mirrors the row content-service writes in ListApprovals. There is no
// `title` field — the card falls back to the trimmed prompt, then the
// caption, the same way the Inbox does.
type ApprovalItem = {
  id: string;
  brand_id: string;
  platform: string;
  prompt?: string | null;
  text_body?: string | null;
};

function ApprovalsMini() {
  const { i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { data: brandsRes } = useBrands();
  const { data: approvalsRes } = useQuery({
    queryKey: ["approvals", { limit: 4 }],
    // Envelope key is `approvals`, not `data` — content-service writes
    // {"approvals": [...], "total": N}. Reading `.data` meant this panel
    // was permanently empty and the count permanently 0.
    queryFn: () => api<{ approvals: ApprovalItem[]; total?: number }>("/approvals?limit=4"),
    staleTime: 15_000,
  });
  const brandMap = new Map(brandsRes?.data?.map((b) => [b.id, b]) ?? []);
  const items = approvalsRes?.approvals ?? [];
  const total = approvalsRes?.total ?? items.length;
  const tr = i18n.resolvedLanguage === "tr";

  return (
    <div className="card" style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 14 }}>
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>— {tr ? "Onay bekleyen" : "Awaiting approval"}</div>
          <h2 style={{ fontSize: 16 }}>
            {tr ? `${total} gönderi` : `${total} across ${new Set(items.map((i) => i.brand_id)).size} brands`}
          </h2>
        </div>
        <button className="btn btn-secondary btn-sm" onClick={() => navigate({ to: "/app/inbox" })}>
          {tr ? "İncele →" : "Review →"}
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.length === 0 ? (
          <div style={{ color: "var(--mu)", fontSize: 12, padding: "8px 0" }}>
            {tr ? "Bekleyen gönderi yok." : "No posts pending."}
          </div>
        ) : null}
        {items.map((p) => {
          const b = brandMap.get(p.brand_id);
          return (
            <div
              key={p.id}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px dashed var(--b1)",
              }}
            >
              {b ? <BrandMark brand={b} size={20} /> : <div style={{ width: 20, height: 20 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {p.prompt || p.text_body || (tr ? "Başlıksız" : "Untitled")}
                </div>
                <div className="mono" style={{ fontSize: 10, color: "var(--mu)" }}>
                  {b?.trade_name ?? "—"} · {p.platform}
                </div>
              </div>
              <PlatformChip platform={p.platform} size={20} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// BrandCard
// ──────────────────────────────────────────────────────────────────────────────

function BrandCard({ brand }: { brand: Brand }) {
  const { i18n } = useTranslation("common");
  const navigate = useNavigate();
  const { setActive } = useActiveBrand();
  const accentSoft = brand.accent_soft_hex ?? "rgba(224,45,28,0.12)";
  const postsWeek = brand.posts_this_week ?? 0;
  const pending = brand.pending_count ?? 0;
  const tr = i18n.resolvedLanguage === "tr";

  // Synthetic sparkline (real data lands when brand.timeseries arrives)
  // Sparkline removed — was a synthetic [4,6,5,9,8,11,...] series
  // on every card. Re-add once brand.timeseries arrives from backend.

  return (
    <div
      className="card card-int"
      onClick={() => {
        setActive(brand);
        void navigate({ to: "/app/brands/$brandId", params: { brandId: brand.id } });
      }}
      style={{
        padding: 18,
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        position: "relative",
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          width: 100,
          height: 100,
          background: `radial-gradient(circle at top right, ${accentSoft}, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ display: "flex", alignItems: "center", gap: 12, position: "relative" }}>
        <BrandMark brand={brand} size={36} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}>{brand.trade_name}</div>
          <div className="mono" style={{ fontSize: 10, color: "var(--mu)", letterSpacing: "0.05em" }}>
            {brand.industry ?? "—"}
          </div>
        </div>
        <span className="btn-icon" onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex" }}>
          <Icons.More size={14} />
        </span>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div
            className="mono"
            style={{ fontSize: 9, color: "var(--mu)", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}
          >
            {tr ? "Bu hafta" : "This week"}
          </div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span className="t-num" style={{ fontSize: 22 }}>{postsWeek}</span>
            <span style={{ fontSize: 11, color: "var(--tx2)" }}>{tr ? "gönderi" : "posts"}</span>
          </div>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {(brand.connected_platforms ?? []).map((pl) => (
          <PlatformChip key={pl} platform={pl} size={18} />
        ))}
        <div style={{ flex: 1 }} />
        {pending > 0 ? (
          <span className="badge badge-amber">
            <span className="dot" />
            {pending} {tr ? "bekliyor" : "pending"}
          </span>
        ) : (
          <span className="badge badge-green">
            <span className="dot" />
            {tr ? "Temiz" : "Clear"}
          </span>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Empty / utility
// ──────────────────────────────────────────────────────────────────────────────

function EmptyBrands() {
  const { i18n } = useTranslation("common");
  const navigate = useNavigate();
  const tr = i18n.resolvedLanguage === "tr";
  return (
    <div className="card" style={{ padding: "64px 24px", textAlign: "center" }}>
      <div className="eyebrow" style={{ marginBottom: 12, justifyContent: "center" }}>
        {tr ? "Boş workspace" : "Empty workspace"}
      </div>
      <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 700, marginBottom: 8 }}>
        {tr ? "İlk markanı ekle" : "Add your first brand"}
      </div>
      <div style={{ color: "var(--tx2)", fontSize: 13, maxWidth: 380, margin: "0 auto 16px" }}>
        {tr
          ? "Markanı bir paragrafta tanıt — AI sektörünü, ses tonunu, hedef kitleyi ve içerik konularını taslak hâlinde hazırlar."
          : "Describe your brand in a paragraph — AI drafts industry, tone of voice, target audience and content topics."}
      </div>
      <button className="btn btn-primary" onClick={() => navigate({ to: "/app/brands/new" })}>
        <Icons.Plus size={14} /> {tr ? "Yeni marka" : "New brand"}
      </button>
    </div>
  );
}

function useGreeting(name?: string, lang?: string) {
  const hour = new Date().getHours();
  const tr = lang === "tr";
  const first = (name ?? "").split(" ")[0] || (tr ? "merhaba" : "there");
  if (tr) {
    if (hour < 5) return `İyi geceler, ${first}.`;
    if (hour < 12) return `Günaydın, ${first}.`;
    if (hour < 18) return `İyi günler, ${first}.`;
    return `İyi akşamlar, ${first}.`;
  }
  if (hour < 12) return `Good morning, ${first}.`;
  if (hour < 18) return `Good afternoon, ${first}.`;
  return `Good evening, ${first}.`;
}
